import {
  createWebinarSuggestion as createWebinarSuggestionService,
  deleteWebinarSuggestion as deleteWebinarSuggestionService,
  fetchWebinarSuggestion as fetchWebinarSuggestionService,
  fetchWebinarSuggestions as fetchWebinarSuggestionsService,
  toggleWebinarSuggestionLike as toggleWebinarSuggestionLikeService,
  updateWebinarSuggestion as updateWebinarSuggestionService,
} from '@core/services/webinar-suggestions/webinar-suggestions-service'
import { AuthenticationService } from '@core/services/authentication/authentication-service'
import {
  getAuthenticatedUidOrThrow,
  loadCurrentUserProfile,
} from '@core/services/user/user-service'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type {
  CreateWebinarSuggestionInput,
  UpdateWebinarSuggestionPatch,
  WebinarSuggestion,
} from '@domain/models/webinar-suggestions'

export class WebinarSuggestionsFeature {
  public static async submitWebinarSuggestion(
    input: CreateWebinarSuggestionInput,
  ): Promise<string> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      const currentUser = await loadCurrentUserProfile(uid)
      return await createWebinarSuggestionService(input, currentUser)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to submit webinar suggestion.',
        underlyingException: error,
      })
    }
  }

  public static async getWebinarSuggestions(
    maxCount?: number,
  ): Promise<WebinarSuggestion[]> {
    try {
      const currentUserId = AuthenticationService.getInstance().userId ?? undefined
      return await fetchWebinarSuggestionsService(currentUserId, maxCount)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch webinar suggestions.',
        underlyingException: error,
      })
    }
  }

  public static async getWebinarSuggestion(
    suggestionId: string,
  ): Promise<WebinarSuggestion> {
    try {
      const currentUserId = AuthenticationService.getInstance().userId ?? undefined
      return await fetchWebinarSuggestionService(suggestionId, currentUserId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch webinar suggestion "${suggestionId}".`,
        underlyingException: error,
      })
    }
  }

  public static async toggleWebinarSuggestionLike(
    suggestionId: string,
  ): Promise<boolean> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      return await toggleWebinarSuggestionLikeService(suggestionId, uid)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to toggle webinar suggestion like "${suggestionId}".`,
        underlyingException: error,
      })
    }
  }

  public static async updateWebinarSuggestion(
    suggestionId: string,
    patch: UpdateWebinarSuggestionPatch,
  ): Promise<void> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      const currentUser = await loadCurrentUserProfile(uid)
      await updateWebinarSuggestionService(suggestionId, patch, currentUser)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to update webinar suggestion "${suggestionId}".`,
        underlyingException: error,
      })
    }
  }

  public static async deleteWebinarSuggestion(suggestionId: string): Promise<void> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      const currentUser = await loadCurrentUserProfile(uid)
      await deleteWebinarSuggestionService(suggestionId, currentUser)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to delete webinar suggestion "${suggestionId}".`,
        underlyingException: error,
      })
    }
  }
}

export async function submitWebinarSuggestion(
  input: CreateWebinarSuggestionInput,
): Promise<string> {
  return WebinarSuggestionsFeature.submitWebinarSuggestion(input)
}

export async function getWebinarSuggestions(
  maxCount?: number,
): Promise<WebinarSuggestion[]> {
  return WebinarSuggestionsFeature.getWebinarSuggestions(maxCount)
}

export async function getWebinarSuggestion(
  suggestionId: string,
): Promise<WebinarSuggestion> {
  return WebinarSuggestionsFeature.getWebinarSuggestion(suggestionId)
}

export async function toggleWebinarSuggestionLike(
  suggestionId: string,
): Promise<boolean> {
  return WebinarSuggestionsFeature.toggleWebinarSuggestionLike(suggestionId)
}

export async function updateExistingWebinarSuggestion(
  suggestionId: string,
  patch: UpdateWebinarSuggestionPatch,
): Promise<void> {
  return WebinarSuggestionsFeature.updateWebinarSuggestion(suggestionId, patch)
}

export async function deleteExistingWebinarSuggestion(
  suggestionId: string,
): Promise<void> {
  return WebinarSuggestionsFeature.deleteWebinarSuggestion(suggestionId)
}
